import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';

import { useDeviceWidth } from 'app/hooks/use-device-size';
import { useUISize } from 'app/utils/web-helpers';
import { useAnimationStyle } from 'app/screens/intro/present-community';
import React, { useEffect, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  type FlatList,
  type ImageRequireSource,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { Image } from 'expo-image';
import Animated, { useSharedValue, type SharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconArrowRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

const AnimatedFlatList = Animated.FlatList;

export interface CardSliderItem {
  id: string;
  topText: React.ReactNode;
  image: ImageRequireSource;
  bottomText: React.ReactNode;
  backgroundImage: ImageRequireSource;
}

interface SliderCardProps {
  item: CardSliderItem;
  index: number;
  scrollX: SharedValue<number>;
  itemWidth: number;
  onArrowPress?: () => void;
  showArrow?: boolean;
  contentPaddingBottom?: number;
}

function SliderCard({
  item,
  index,
  scrollX,
  itemWidth,
  onArrowPress,
  showArrow = false,
  contentPaddingBottom = 0,
}: SliderCardProps) {
  const { top } = useSafeAreaInsets();
  const { isLargeUI } = useUISize();
  const backgroundAnimatedStyle = useAnimationStyle(itemWidth, index, scrollX, 0, 1);
  const contentAnimatedStyle = useAnimationStyle(itemWidth, index, scrollX, 0.4, 1, 50, 0);

  return (
    <>
      {showArrow && Platform.OS === 'web' && onArrowPress ? (
        <View style={{ position: 'absolute', top: 20, right: 20, zIndex: 100 }}>
          <Pressable onPress={onArrowPress}>
            <IconArrowRight strokeWidth={ICON_STROKE_WIDTH} color="white" />
          </Pressable>
        </View>
      ) : null}

      <Animated.View
        style={[
          {
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          },
          backgroundAnimatedStyle,
        ]}
      >
        <Image source={item.backgroundImage} style={StyleSheet.absoluteFill} contentFit="cover" blurRadius={10} />
      </Animated.View>

      <View
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }}
      />

      <Animated.View style={[{ flex: 1 }, contentAnimatedStyle]}>
        <Column
          top={top}
          flex={1}
          alignItems="center"
          style={{
            paddingBottom: contentPaddingBottom + theme.spacing['spacing.general.x16'],
            paddingTop: isLargeUI ? theme.spacing['spacing.general.x12'] : theme.spacing['spacing.general.x4'],
          }}
          paddingHorizontal="spacing.general.x4"
          gap="spacing.general.x4"
        >
          <Text textAlign="center" fontSize={24} color="white" alignItems="center" width={isLargeUI ? '80%' : '100%'}>
            {item.topText}
          </Text>
          <PCImage
            source={item.image}
            style={{
              width: '100%',
              flex: 1,
              maxWidth: 524,
              resizeMode: 'contain',
              marginBottom: theme.spacing['spacing.general.x4'],
            }}
          />

          <Row
            alignItems="center"
            justifyContent="center"
            padding="spacing.general.x4"
            style={{ minHeight: 80 }}
            width="100%"
          >
            <Text textAlign="center" fontSize={20} color="white" alignItems="center" width={isLargeUI ? '80%' : '100%'}>
              {item.bottomText}
            </Text>
          </Row>
        </Column>
      </Animated.View>
    </>
  );
}

export interface CardSliderProps {
  items: CardSliderItem[];
  onItemChange?: (item: CardSliderItem, index: number) => void;
  autoScrollDelay?: number;
  autoScrollToNext?: boolean;
  showArrows?: boolean;
  renderFooter?: (currentItem: CardSliderItem, currentIndex: number) => React.ReactNode;
  containerWidth?: number;
  contentPaddingBottom?: number;
}

export function CardSlider({
  items,
  onItemChange,
  autoScrollDelay = 6000,
  autoScrollToNext = false,
  showArrows = false,
  renderFooter,
  containerWidth,
  contentPaddingBottom,
}: CardSliderProps) {
  const overflow = 0;
  const itemMargin = 0;
  const deviceWidth = useDeviceWidth();
  const { isSmallUI } = useUISize();
  const w = (containerWidth && containerWidth > 0 ? containerWidth : deviceWidth) - 2 * itemMargin;
  const itemWidth = w / (1 + overflow);
  const startScroll = 0;

  const scrollX = useSharedValue(0);
  const snapToOffsets = items.map((_, i) => startScroll + i * itemWidth);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const flatListRef = useRef<FlatList>(null);

  const setIndex = (index: number) => {
    const clampedIndex = Math.max(0, Math.min(index, items.length - 1));
    setSelectedIndex(clampedIndex);
    onItemChange?.(items[clampedIndex]!, clampedIndex);
  };

  const scrollHandler = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetX = e.nativeEvent.contentOffset.x;
    scrollX.set(offsetX);

    const index = Math.abs(Math.round(offsetX / itemWidth));
    setIndex(index);
  };

  const scrollToIndex = (index: number) => {
    const clampedIndex = Math.max(0, Math.min(index, items.length - 1));
    flatListRef.current?.scrollToOffset({
      offset: snapToOffsets[clampedIndex]!,
      animated: true,
    });
  };

  // Auto-scroll effect
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!autoScrollToNext) return;

    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      const nextIndex = (selectedIndex + 1) % items.length;
      flatListRef.current?.scrollToOffset({
        offset: startScroll + nextIndex * itemWidth,
        animated: true,
      });
    }, autoScrollDelay);

    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [autoScrollDelay, autoScrollToNext, itemWidth, items.length, selectedIndex]);

  if (items.length === 0) {
    return null;
  }

  return (
    <View style={{ flex: 1 }}>
      <AnimatedFlatList
        bounces
        data={items}
        decelerationRate={Platform.OS === 'ios' ? 0.99 : 0.95}
        disableIntervalMomentum
        horizontal
        scrollEnabled={isSmallUI}
        keyExtractor={(item) => item.id}
        onScroll={scrollHandler}
        scrollEventThrottle={16}
        ref={flatListRef}
        renderItem={({ item, index }) => (
          <View
            style={{
              flex: 1,
              width: itemWidth,
              height: '100%',
            }}
          >
            <SliderCard
              contentPaddingBottom={contentPaddingBottom}
              item={item}
              index={index}
              scrollX={scrollX}
              itemWidth={itemWidth}
              onArrowPress={() => scrollToIndex(selectedIndex + 1)}
              showArrow={showArrows}
            />
          </View>
        )}
        showsHorizontalScrollIndicator={false}
        snapToOffsets={snapToOffsets}
        style={{ flex: 1 }}
      />

      {renderFooter?.(items[selectedIndex]!, selectedIndex)}
    </View>
  );
}
